import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { useInView } from "react-intersection-observer";
import { useSpring, animated } from "@react-spring/web";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBrain, 
  faCode, 
  faShieldHalved 
} from "@fortawesome/free-solid-svg-icons";

const SubSection: React.FC<{
  title: string;
  description: string;
  imageUrl: string;
  isReversed?: boolean;
  theme: string;
  icon: any;
}> = ({ title, description, imageUrl, isReversed = false, theme, icon }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const animation = useSpring({
    from: { 
      opacity: 0, 
      transform: `translateX(${isReversed ? '-50px' : '50px'})` 
    },
    to: { 
      opacity: inView ? 1 : 0, 
      transform: inView ? 'translateX(0px)' : `translateX(${isReversed ? '-50px' : '50px'})` 
    },
    config: { tension: 120, friction: 14 },
    delay: 200,
  });

  const isDark = theme === "dark";

  return (
    <animated.div 
      ref={ref} 
      style={animation} 
      className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} 
        gap-8 md:gap-16 lg:gap-24 items-center mb-16 md:mb-24 lg:mb-32 px-4 sm:px-6 lg:px-8`}
    >
      {/* Text Content */}
      <div className="flex-1 space-y-4 md:space-y-6 max-w-xl w-full">
        <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
          <div className={`
            w-10 h-10 md:w-12 md:h-12 
            rounded-xl 
            flex items-center justify-center
            ${isDark 
              ? "bg-green-500/10 border border-green-500/30" 
              : "bg-green-900/10 border border-green-900/30"
            }
          `}>
            <FontAwesomeIcon 
              icon={icon} 
              className={`w-5 h-5 md:w-6 md:h-6 ${
                isDark ? "text-green-400" : "text-green-900"
              }`} 
            />
          </div>
          <h3 className={`text-2xl md:text-3xl font-bold ${
            isDark ? "text-green-400" : "text-green-900"
          }`}>
            {title}
          </h3>
        </div>
        <div className={`w-16 md:w-20 h-1 rounded-full ${
          isDark 
            ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
            : "bg-gradient-to-r from-green-900/50 to-green-700/50"
        }`} />
        <p className={`text-lg md:text-xl leading-relaxed ${
          isDark ? "text-gray-300" : "text-gray-700"
        }`}>
          {description}
        </p>
      </div>

      {/* Image Container */}
      <div className="flex-1 w-full md:pl-0 lg:pl-8">
        <div className={`
          relative 
          rounded-2xl 
          overflow-hidden 
          group
          transform 
          transition-all 
          duration-500
          hover:scale-[1.02]
          ${isDark 
            ? "border-2 border-green-500/30 hover:border-green-400" 
            : "border-2 border-green-900/30 hover:border-green-800"
          }
        `}>
          <div className={`
            absolute inset-0 
            bg-gradient-to-br 
            transition-opacity 
            duration-500
            opacity-0 
            group-hover:opacity-100
            ${isDark 
              ? "from-green-500/20 to-transparent" 
              : "from-green-900/20 to-transparent"
            }
          `} />
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover aspect-video"
          />
        </div>
      </div>
    </animated.div>
  );
};

const Solutions = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const content = {
    section1: {
      title: language === "en" ? "AI Intelligence as a Commodity" : "Intelligence Artificielle Accessible",
      description: language === "en" 
        ? "We transform complex AI capabilities into practical business solutions through advanced prompt engineering, hallucination mitigation frameworks, and Retrieval-Augmented Generation (RAG) systems. Our standardized yet customizable approaches make enterprise-grade AI accessible for all organizations."
        : "Nous transformons les capacités complexes de l'IA en solutions métiers pratiques grâce à l'ingénierie de prompts avancée, des systèmes de réduction des hallucinations et des architectures RAG. Nos approches standardisées mais personnalisables rendent l'IA de niveau entreprise accessible à toutes les organisations.",
      imageUrl: "https://cdn.pixabay.com/photo/2024/04/09/16/30/ai-generated-8686301_1280.jpg",
      icon: faBrain
    },
    section2: {
      title: language === "en" ? "End-to-End Expertise" : "Expertise de Bout en Bout",
      description: language === "en"
        ? "Combining decades of software engineering and data science experience, we deliver complete solutions from concept to production. Our full lifecycle management ensures seamless integration, robust testing, and continuous optimization of AI systems in real-world environments."
        : "Combinant des décennies d'expérience en ingénierie logicielle et science des données, nous livrons des solutions complètes du concept à la production. Notre gestion du cycle de vie complet garantit une intégration transparente, des tests rigoureux et une optimisation continue des systèmes d'IA en environnements réels.",
      imageUrl: "https://cdn.pixabay.com/photo/2019/08/09/08/21/data-protection-4394633_1280.jpg",
      icon: faCode
    },
    section3: {
      title: language === "en" ? "GDPR-Compliant Privacy" : "Confidentialité RGPD",
      description: language === "en"
        ? "Maintain full data sovereignty with our private infrastructure solutions. We deploy open-source LLMs and automation tools within your secure environment, offering cost-efficient hosting alternatives that meet strict European compliance requirements without compromising functionality."
        : "Maintenez la souveraineté totale de vos données avec nos solutions d'infrastructure privée. Nous déployons des LLMs open-source et des outils d'automatisation dans votre environnement sécurisé, offrant des alternatives d'hébergement économiques respectant les strictes exigences européennes.",
      imageUrl: "https://cdn.pixabay.com/photo/2021/01/02/12/39/lock-5881443_1280.jpg",
      icon: faShieldHalved
    }
  };

  return (
    <section 
      id="solutions"
      className="relative py-32 px-8 overflow-hidden">
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-32">
          <h2 className={`
            text-4xl md:text-5xl lg:text-6xl 
            font-bold 
            ${isDark 
              ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
              : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
            }
            bg-clip-text 
            text-transparent
            mb-6
            animate-gradient-x
          `}
          style={{ lineHeight: '1.2', paddingBottom: '10px' }}>
            {language === "en" ? "Our Solutions" : "Nos Solutions"}
          </h2>
          <div className={`w-24 h-1 mx-auto rounded-full mt-6 ${
            isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
              : "bg-gradient-to-r from-green-900/50 to-green-700/50"
          }`} />
        </div>

        {/* Subsections */}
        <SubSection
          title={content.section1.title}
          description={content.section1.description}
          imageUrl={content.section1.imageUrl}
          theme={theme}
          icon={content.section1.icon}
        />
        <SubSection
          title={content.section2.title}
          description={content.section2.description}
          imageUrl={content.section2.imageUrl}
          isReversed
          theme={theme}
          icon={content.section2.icon}
        />
        <SubSection
          title={content.section3.title}
          description={content.section3.description}
          imageUrl={content.section3.imageUrl}
          theme={theme}
          icon={content.section3.icon}
        />
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
        <div className={`absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
      </div>
    </section>
  );
};

export default Solutions; 